import { useState } from "react"
import { Box, LinearProgress, Typography } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { CopScanLog } from "./server/cop-events.mjs"

const TYPE_COLORS = {
    info: "text.secondary",
    success: "success.main",
    warn: "warning.main",
}

const TYPE_ICONS = {
    info: "mdi:information-outline",
    success: "mdi:check-circle-outline",
    warn: "mdi:alert-outline",
}

export function CopScanProgress({ group }) {
    const assessed = group.scanAssessed ?? 0
    const withFindings = group.scanWithFindings ?? 0
    const findingsCount = group.scanFindingsCount ?? 0
    const total = group.scanTotal ?? null
    const pct = total ? Math.round((assessed / total) * 100) : null

    const countText =
        total !== null
            ? `${assessed.toLocaleString()} / ${total.toLocaleString()} schedules assessed`
            : `${assessed.toLocaleString()} schedules assessed`

    const findingsText =
        withFindings > 0
            ? ` · ${withFindings.toLocaleString()} schedule${withFindings !== 1 ? "s" : ""} with ${findingsCount.toLocaleString()} finding${findingsCount !== 1 ? "s" : ""}`
            : ""

    const [messages, setMessages] = useState([])

    CopScanLog.useEvent(handleMessage, [group._id])

    return (
        <Box
            sx={{
                mb: 2,
                p: 1.5,
                bgcolor: "rgba(26,140,180,0.08)",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "rgba(26,140,180,0.2)",
            }}
        >
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
                <Typography variant="body2" fontWeight={500}>
                    Scanning in progress…
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {pct !== null ? `${pct}%` : ""}
                </Typography>
            </Box>

            <LinearProgress
                variant={pct !== null ? "determinate" : "indeterminate"}
                value={pct ?? undefined}
                sx={{ borderRadius: 0.5, mb: 0.75 }}
            />

            <Typography variant="caption" color="text.secondary">
                {countText}
                {findingsText}
            </Typography>

            <ScanLog messages={messages} />
        </Box>
    )

    function handleMessage(data) {
        if (data.codeOfPracticeId !== group._id) return
        setMessages((prev) => [data, ...prev.slice(0, 99)])
    }
}

function ScanLog({ messages }) {
    if (messages.length === 0) return null

    return (
        <Box
            sx={{
                mt: 1,
                pt: 1,
                borderTop: "1px solid",
                borderColor: "rgba(26,140,180,0.15)",
                maxHeight: 80,
                overflowY: "auto",
            }}
        >
            {messages.map((msg, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, py: 0.15 }}>
                    <Iconify
                        icon={TYPE_ICONS[msg.type] ?? TYPE_ICONS.info}
                        sx={{ color: TYPE_COLORS[msg.type] ?? TYPE_COLORS.info, fontSize: 13, mt: 0.2, flexShrink: 0 }}
                    />
                    <Typography
                        variant="caption"
                        color={TYPE_COLORS[msg.type] ?? TYPE_COLORS.info}
                        sx={{ lineHeight: 1.4 }}
                    >
                        {msg.message}
                    </Typography>
                </Box>
            ))}
        </Box>
    )
}
